import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FiUser, FiPhone, FiMapPin } from 'react-icons/fi';

const EditProfile: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        mobile: '',
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success && data.user) {
                    setFormData({
                        username: data.user.username || '',
                        address: data.user.address || '',
                        city: data.user.city || '',
                        state: data.user.state || '',
                        pincode: data.user.pincode || '',
                        mobile: data.user.mobile || '',
                    });
                } else {
                    navigate('/login');
                }
            } catch (err) {
                navigate('/login');
            }
        };

        const token = localStorage.getItem('authToken');
        if (!token) {
            navigate('/login');
        } else {
            fetchData();
        }
    }, [navigate]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name === 'mobile') {
            return setFormData(prev => ({ ...prev, mobile: value.replace(/\D/g, '').slice(0, 10) }));
        }

        if (name === 'pincode') {
            return setFormData(prev => ({ ...prev, pincode: value.replace(/\D/g, '').slice(0, 6) }));
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const res = await fetch(`${API_BASE_URL}/auth/edit-profile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                },
                body: JSON.stringify(formData),
            });

            const data = await res.json();
            if (res.ok) {
                Swal.fire({
                    title: 'Updated',
                    text: data.message,
                    icon: 'success',
                    customClass: {
                        confirmButton: 'bg-indigo-600 text-white px-4 py-2 rounded',
                    },
                    buttonsStyling: false,
                });
            } else {
                Swal.fire('Error', data.error || 'Update failed', 'error');
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Something went wrong', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-lg mx-auto mt-20 p-6 bg-white shadow rounded">
            <h2 className="text-2xl font-bold text-center text-indigo-700 mb-6">Edit Profile</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Username */}
                <div>
                    <label className="block text-sm text-gray-600 mb-1">Full Name</label>
                    <div className="relative">
                        <FiUser className="absolute top-3 left-3 text-gray-400" />
                        <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            className="pl-10 w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                            required
                        />
                    </div>
                </div>

                {/* Address */}
                <div>
                    <label className="block text-sm text-gray-600 mb-1">Address</label>
                    <div className="relative">
                        <FiMapPin className="absolute top-3 left-3 text-gray-400" />
                        <textarea
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            rows={3}
                            className="pl-10 w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                            required
                        />
                    </div>
                </div>

                {/* City */}
                <div>
                    <label className="block text-sm text-gray-600 mb-1">City</label>
                    <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                        required
                    />
                </div>

                {/* State */}
                <div>
                    <label className="block text-sm text-gray-600 mb-1">State</label>
                    <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                        required
                    />
                </div>

                {/* Pincode */}
                <div>
                    <label className="block text-sm text-gray-600 mb-1">Pincode</label>
                    <input
                        type="text"
                        name="pincode"
                        inputMode="numeric"
                        value={formData.pincode}
                        onChange={handleChange}
                        placeholder="6-digit code"
                        className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                        required
                    />
                </div>

                {/* Mobile */}
                <div>
                    <label className="block text-sm text-gray-600 mb-1">Mobile</label>
                    <div className="relative">
                        <FiPhone className="absolute top-3 left-3 text-gray-400" />
                        <input
                            type="tel"
                            name="mobile"
                            inputMode="numeric"
                            value={formData.mobile}
                            onChange={handleChange}
                            placeholder="10-digit number"
                            className="pl-10 w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                            required
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700"
                >
                    {loading ? 'Saving...' : 'Save Changes'}
                </button>
            </form>
        </div>
    );
};

export default EditProfile;
